import Board from "components/Common/Board/Board";
import React, { useState } from "react";
import { Table } from "components/Common/Table/Table";
import UserTableRow from "./UserTableRow";
import styled from "styled-components";
import Pagination from "components/foundation/Pagination";
import { FormProvider, useForm } from "react-hook-form";
import useUsersSearchQuery from "features/user/hooks/queries/useUsersSearchQuery";
import useUsersAutocompleteQuery from "features/user/hooks/queries/useUsersAutocompleteQuery";
import { match } from "ts-pattern";
import SearchEmpty from "components/Common/Empty/SearchEmpty";
import TableInputMobile from "components/Common/Table/TableInputMobile";
import { SearchIcon, DateIcon } from "assets/icons";
import TableDatePickerMobile from "components/Common/Table/TableDatePickerMobile";
import removeEmptyFields from "utils/removeEmptyFields";
import dayjs from "dayjs";
import { LOCAL_TIME_FORMAT } from "config/constant";

interface FormData {
  searchType: "userName" | "companyName";
  search: string;
  startDate?: Date;
  endDate?: Date;
}

const UserDashboardMobilePage = () => {
  const form = useForm<FormData>({
    defaultValues: {
      searchType: "userName",
      search: "",
      startDate: undefined,
      endDate: undefined,
    },
  });

  const [searchOption, setSearchOption] = useState<FormData>({
    searchType: "userName",
    search: "",
    startDate: undefined,
    endDate: undefined,
  });
  const [page, setPage] = useState(1);

  const { data } = useUsersSearchQuery(
    removeEmptyFields({
      page: page - 1,
      size: 10,
      [searchOption.searchType]: searchOption.search,
      endDate:
        searchOption.endDate &&
        dayjs(searchOption.endDate).format(LOCAL_TIME_FORMAT),
      startDate:
        searchOption.startDate &&
        dayjs(searchOption.startDate).format(LOCAL_TIME_FORMAT),
    }),
  );

  const [type, keyword] = [form.watch("searchType"), form.watch("search")];

  const { data: autoCompleteData } = useUsersAutocompleteQuery({
    type,
    keyword,
  });

  const usersList = data.data.result;

  const handleSearch = (data: FormData) => {
    setSearchOption(data);
  };

  return (
    <UserDashboardPageLayout>
      <ContentBoard>
        <Table.Header>
          <Table.TotalCount>총 {usersList.totalElements}건</Table.TotalCount>

          <form onSubmit={form.handleSubmit(handleSearch)}>
            <FormProvider {...form}>
              <div
                style={{
                  display: "flex",
                  gap: "1rem",
                }}
              >
                <TableInputMobile
                  searchType={[
                    {
                      label: "이름",
                      value: "userName",
                    },
                    {
                      label: "회사",
                      value: "companyName",
                    },
                  ]}
                  autocomplete={autoCompleteData?.data.result.results.map(
                    (i) => ({
                      label: i,
                      value: i,
                    }),
                  )}
                >
                  <SearchIcon />
                  검색
                </TableInputMobile>
                <TableDatePickerMobile
                  isSelected={!!searchOption.startDate}
                  onDateRangeSelected={({ start, end }) => {
                    setSearchOption((pre) => ({
                      ...pre,
                      startDate: start,
                      endDate: end,
                    }));
                  }}
                >
                  <DateIcon />
                  등록일
                </TableDatePickerMobile>
              </div>
            </FormProvider>
          </form>
        </Table.Header>

        {match(usersList.totalElements)
          .with(0, () => <SearchEmpty />)
          .otherwise(() => (
            <>
              <Table.Layout>
                <Table.Table columns="auto auto auto 12rem" width="100%">
                  <Table.Thead>
                    <Table.Th>이름</Table.Th>
                    <Table.Th>회사</Table.Th>
                    <Table.Th>가입일</Table.Th>
                    <Table.Th>승인여부</Table.Th>
                  </Table.Thead>
                  <Table.Tbody>
                    {usersList.contents.map((user) => (
                      <UserTableRow
                        id={user.uuid}
                        key={user.uuid}
                        name={user.name}
                        company={user.companyName}
                        joinDate={user.createdAt}
                        userStatus={user.approveStatus}
                      />
                    ))}
                  </Table.Tbody>
                </Table.Table>
              </Table.Layout>
              <Pagination
                currentPage={page}
                onClickPageButton={(page) => setPage(page)}
                lastPage={usersList.totalPages}
                style={{
                  margin: "auto",
                }}
              />
            </>
          ))}
      </ContentBoard>
    </UserDashboardPageLayout>
  );
};

export default UserDashboardMobilePage;

const UserDashboardPageLayout = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2rem;
`;

const ContentBoard = styled(Board)`
  padding: 2rem;
  display: flex;
  flex-direction: column;
  gap: 2rem;
`;
